import React from 'react'
import { Button, Modal, ModalBody, ModalFooter } from 'reactstrap'
import PropTypes from 'prop-types'
import { JETMS } from '@jet/jetms-client-sdk/dist/window'

const isJetms = typeof JETMS !== 'undefined'

const modalPropTypes = {
  isOpen: PropTypes.bool.isRequired,
  body: PropTypes.object.isRequired,
  primaryButton: PropTypes.object.isRequired,
  toggle: PropTypes.func.isRequired,
  onOpened: PropTypes.func,
  dialogClassName: PropTypes.string
}

const FOCUSABLE_SELECTOR = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'

/**
 * Inline modal used in JETMS (single-spa) mode.
 *
 * Reactstrap v6 always portals modals to document.body, which falls outside
 * the postcss-single-spa-scoped CSS scope and loses all Bootstrap styling.
 * This renders standard Bootstrap markup inline so scoped styles apply.
 */
class InlineModal extends React.Component {
  constructor(props) {
    super(props)
    this.dialogRef = React.createRef()
    this.lastFocusedElement = null
    this.handleKeyDown = this.handleKeyDown.bind(this)
    this.handleClick = this.handleClick.bind(this)
  }

  componentDidMount() {
    if (this.props.isOpen) {
      this.onOpen()
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.isOpen && !prevProps.isOpen) {
      this.onOpen()
    }
    if (!this.props.isOpen && prevProps.isOpen) {
      this.onClose()
    }
  }

  componentWillUnmount() {
    this.onClose()
  }

  onOpen() {
    this.lastFocusedElement = document.activeElement
    if (this.props.onOpened) this.props.onOpened()
    if (this.dialogRef.current) this.dialogRef.current.focus()
    document.body.style.overflow = 'hidden'
    document.addEventListener('keydown', this.handleKeyDown)
    document.addEventListener('click', this.handleClick)
  }

  onClose() {
    document.body.style.overflow = ''
    document.removeEventListener('keydown', this.handleKeyDown)
    document.removeEventListener('click', this.handleClick)
    if (this.lastFocusedElement) this.lastFocusedElement.focus()
  }

  handleKeyDown(e) {
    if (e.key === 'Escape' && this.props.toggle) {
      this.props.toggle(e)
      return
    }
    if (e.key !== 'Tab' || !this.dialogRef.current) return
    const focusable = this.dialogRef.current.querySelectorAll(FOCUSABLE_SELECTOR)
    if (!focusable.length) return
    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    if (e.shiftKey && document.activeElement === first) {
      last.focus()
      e.preventDefault()
    } else if (!e.shiftKey && document.activeElement === last) {
      first.focus()
      e.preventDefault()
    }
  }

  handleClick(e) {
    if (this.dialogRef.current && e.target === this.dialogRef.current && this.props.toggle) {
      this.props.toggle(e)
    }
  }

  render() {
    if (!this.props.isOpen) return null

    const { body, toggle, primaryButton, dialogClassName } = this.props

    return (
      <React.Fragment>
        <dialog
          ref={this.dialogRef}
          className={`modal fade show ${dialogClassName || ''}`}
          style={{ display: 'block', border: 'none', padding: 0, background: 'transparent', maxWidth: 'none', maxHeight: 'none' }}
          open
          aria-modal="true"
          tabIndex="-1"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content h6">
              <div className="modal-body">
                {body}
              </div>
              <div className="modal-footer">
                <Button color="secondary" onClick={toggle}>Cancel</Button>
                {primaryButton}
              </div>
            </div>
          </div>
        </dialog>
        <div className="modal-backdrop fade show" />
      </React.Fragment>
    )
  }
}

InlineModal.propTypes = modalPropTypes

const ModalComponent = ({ isOpen, body, toggle, primaryButton, onOpened, dialogClassName}) => {
  if (isJetms) {
    return <InlineModal
      isOpen={isOpen}
      body={body}
      toggle={toggle}
      primaryButton={primaryButton}
      onOpened={onOpened}
      dialogClassName={dialogClassName}
    />
  }

  return <Modal isOpen={isOpen} toggle={toggle} contentClassName="h6" centered onOpened={onOpened} className={dialogClassName}>
    <ModalBody>
      {body}
    </ModalBody>
    <ModalFooter>
      <Button color="secondary" onClick={toggle}>Cancel</Button>
      {primaryButton}
    </ModalFooter>
  </Modal>
}

ModalComponent.propTypes = modalPropTypes

export { InlineModal }
export default ModalComponent



// WEBPACK FOOTER //
// ./src/components/modal/index.js